/**
 * The Charter Board's live data, its dashboard section and its station tab (M5 plan C17, spec §15.2, §19).
 *
 * - **Data** (`createBoardData`): the galaxy view (claim summaries, fronts), the relations and gate views, and the
 *   claim details and votes decoded from a strategic save (`boardSource.ts`); the current system's live
 *   `SystemEconomyView.claim` wins over the decode. It reads on `refresh()` (the Board's open and, while open,
 *   every `board.json refreshS`) and pushes each new `BoardInput` to its listeners.
 * - **Dashboard section** (`createCharterSection`, Intel › Charter; W3 card M1, replacing the K modal): the shell
 *   owns the frame, the keys and Close; its focus inputs move through the Board's buttons (`useButtonNav`).
 * - **Station tab** (`createCharterStationPanel`): the same Board in station screens (a `StationServicePanel`).
 *
 * UI events only.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { logger } from '../../core/logger'
import type { RegionId } from '../../galaxy/types'
import type { PanelContext, StationServicePanel } from '../../reach/ports'
import type { StrategicClient } from '../../strategic/client'
import { dashboardOpen, type DashSection, type DashSectionProps } from '../dashboard/registry'
import { useButtonNav } from '../strategic/dashNav'
import { mountReact } from '../strategic/mount'
import type { BoardConfig, BoardInput, BoardSystemInfo, ClaimDetail } from './boardModel'
import { decodeBoardCorp, decodeClaimDetails, decodeVotes, saveDay } from './boardSource'
import { CharterBoard } from './CharterBoard'

/** What the Board reads from the strategic client. */
export type BoardClient = Pick<
  StrategicClient,
  'galaxyView' | 'systemView' | 'relations' | 'refreshGalaxyView' | 'save' | 'onGalaxyView' | 'onSystemView' | 'onRelations' | 'onGate'
>

export interface BoardGalaxy {
  systems: readonly { id: string; name: string; region: RegionId }[]
  relays: readonly { id: string; a: string; b: string }[]
}

export interface BoardData {
  readonly input: BoardInput
  readonly relayEnds: ReadonlyMap<string, readonly [string, string]>
  readonly loading: boolean
  /** Re-reads the galaxy view and the save; resolves when the new input was pushed. */
  refresh(): Promise<void>
  /** Follows the live views (call while the Board shows); returns the stop. */
  follow(): () => void
  onChange(fn: (input: BoardInput) => void): () => void
}

export function createBoardData(client: BoardClient, galaxy: BoardGalaxy, dayS: number): BoardData {
  const systems: BoardSystemInfo[] = galaxy.systems.map((s) => ({
    id: s.id,
    name: s.name,
    region: s.region,
  }))
  const systemIds = systems.map((s) => s.id)
  const relayEnds = new Map<string, readonly [string, string]>(galaxy.relays.map((r) => [r.id, [r.a, r.b] as const]))
  let decoded: ReadonlyMap<string, ClaimDetail> = new Map()
  let loading = true
  let input: BoardInput = {
    systems,
    galaxy: client.galaxyView,
    claims: decoded,
    relations: client.relations,
    gate: null,
    votes: [],
    day: 0,
    corp: null,
  }
  const listeners = new Set<(input: BoardInput) => void>()

  function withLiveClaim(base: ReadonlyMap<string, ClaimDetail>): ReadonlyMap<string, ClaimDetail> {
    const sv = client.systemView
    if (sv === null) return base
    const m = new Map(base)
    m.set(sv.systemId, { scores: sv.claim.scores, flip: sv.claim.flip, corp: sv.claim.corp })
    return m
  }

  function push(next: Partial<BoardInput>): void {
    input = { ...input, ...next }
    for (const fn of [...listeners]) fn(input)
  }

  return {
    get input(): BoardInput {
      return input
    },
    relayEnds,
    get loading(): boolean {
      return loading
    },
    async refresh(): Promise<void> {
      try {
        const [g, save] = await Promise.all([client.refreshGalaxyView(), client.save()])
        decoded = decodeClaimDetails(save, systemIds)
        loading = false
        push({
          galaxy: g,
          claims: withLiveClaim(decoded),
          votes: decodeVotes(save),
          day: saveDay(save, dayS),
          corp: decodeBoardCorp(save),
          relations: client.relations ?? input.relations,
        })
      } catch (err: unknown) {
        loading = false
        logger.warn('Charter Board: could not read the Charter registry', err)
        push({
          galaxy: client.galaxyView ?? input.galaxy,
          claims: withLiveClaim(decoded),
        })
      }
    },
    follow(): () => void {
      const offs = [
        client.onGalaxyView((g) => push({ galaxy: g })),
        client.onSystemView(() => push({ claims: withLiveClaim(decoded) })),
        client.onRelations((r) => push({ relations: r })),
        client.onGate((gate) => push({ gate })),
      ]
      return () => {
        for (const off of offs) off()
      }
    },
    onChange(fn: (input: BoardInput) => void): () => void {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
  }
}

// ---------------------------------------------------------------------------------------------
// Intel › Charter (the dashboard section)
// ---------------------------------------------------------------------------------------------

export interface CharterSectionDeps {
  data: BoardData
  config: BoardConfig
  /** A row was chosen (the dashboard has closed): focus the galaxy map. */
  onSelect: (systemId: string) => void
  /** True while the dashboard shows (default: the registry's host). */
  isOpen?: () => boolean
  /** Timers (tests pass fakes). */
  setInterval?: (fn: () => void, ms: number) => number
  clearInterval?: (id: number) => void
}

/**
 * Intel › Charter (`intel.charter`, deep link `charterBoard` = K; dashboard spec §2.2 tab 6). While mounted it
 * follows the live views and re-reads the registry every `board.json refreshS`, but only while the dashboard shows:
 * closed (the shell stays mounted, hidden) it skips the decode, and the reopening render refreshes once.
 */
export function createCharterSection(deps: CharterSectionDeps): DashSection {
  const isOpen = deps.isOpen ?? dashboardOpen
  const setIv = deps.setInterval ?? ((fn: () => void, ms: number): number => window.setInterval(fn, ms))
  const clearIv = deps.clearInterval ?? ((id: number): void => window.clearInterval(id))

  function CharterSectionBody(p: DashSectionProps): ReactNode {
    const [, setTick] = useState(0)
    const root = useRef<HTMLDivElement>(null)
    /** Set while the dashboard was seen closed; the next open render refreshes. */
    const stale = useRef(false)
    useButtonNav(p.nav, root)
    useEffect(() => {
      const bump = (): void => {
        if (isOpen()) setTick((n) => n + 1)
        else stale.current = true
      }
      const iv = setIv(() => {
        if (isOpen()) void deps.data.refresh()
        else stale.current = true
      }, deps.config.refreshS * 1000)
      const offs = [deps.data.onChange(bump), deps.data.follow()]
      void deps.data.refresh()
      return (): void => {
        for (const off of offs) off()
        clearIv(iv)
      }
    }, [])
    useEffect(() => {
      if (stale.current && isOpen()) {
        stale.current = false
        void deps.data.refresh()
      }
    })
    return (
      <div ref={root} className="cb-dash">
        <CharterBoard
          input={deps.data.input}
          config={deps.config}
          relayEnds={deps.data.relayEnds}
          loading={deps.data.loading}
          onSelect={(id) => {
            p.port.close(false)
            deps.onSelect(id)
          }}
        />
      </div>
    )
  }

  return {
    id: 'intel.charter',
    tab: 'intel',
    order: deps.config.dashboard.order,
    label: deps.config.dashboard.label,
    deepLink: 'charterBoard',
    available: () => true,
    component: CharterSectionBody,
  }
}

// ---------------------------------------------------------------------------------------------
// The station tab
// ---------------------------------------------------------------------------------------------

/** After M4's Station (50) and Wire (60) tabs. */
export const CHARTER_PANEL_ORDER = 65

export interface CharterStationPanelDeps {
  data: BoardData
  config: BoardConfig
  onSelect: (systemId: string) => void
}

export function createCharterStationPanel(deps: CharterStationPanelDeps): StationServicePanel {
  return {
    id: 'charter',
    title: 'Charter',
    order: CHARTER_PANEL_ORDER,
    available: (): boolean => true,
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      const mount = mountReact(el, 'sui-mount cb-station')
      const render = (): void => {
        mount.render(
          <CharterBoard
            input={deps.data.input}
            config={deps.config}
            relayEnds={deps.data.relayEnds}
            loading={deps.data.loading}
            onSelect={(id) => {
              ctx.close()
              deps.onSelect(id)
            }}
          />,
        )
        ctx.refresh()
      }
      const offs = [deps.data.onChange(render), deps.data.follow()]
      render()
      void deps.data.refresh()
      return (): void => {
        for (const off of offs) off()
        mount.unmount()
      }
    },
  }
}
